import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { addAddress, updateAddress } from '../../redux/accountSlice';

const AddressFormModal = ({ 
  isOpen, 
  onClose, 
  token, 
  addressesLength = 0, 
  initialAddress = null, 
  forceDefault = false,
  onSuccess 
}) => {
  const dispatch = useDispatch();

  // Form states
  const [shopName, setShopName] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [phone, setPhone] = useState('');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('India');
  const [isDefault, setIsDefault] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [saving, setSaving] = useState(false);

  // Mode: 'add' or 'edit'
  const mode = initialAddress ? 'edit' : 'add';

  // Load initial address if editing
  useEffect(() => {
    if (isOpen) {
      if (initialAddress) {
        setShopName(initialAddress.shopName || '');
        setContactPerson(initialAddress.contactPerson || '');
        setPhone(initialAddress.phone || '');
        setStreet(initialAddress.street || '');
        setCity(initialAddress.city || '');
        setState(initialAddress.state || '');
        setPostalCode(initialAddress.postalCode || '');
        setCountry(initialAddress.country || 'India');
        setIsDefault(initialAddress.isDefault || false);
      } else {
        // Reset form for adding
        setShopName('');
        setContactPerson('');
        setPhone('');
        setStreet('');
        setCity('');
        setState('');
        setPostalCode('');
        setCountry('India');
        setIsDefault(forceDefault || addressesLength === 0);
      }
      setValidationError('');
    }
  }, [isOpen, initialAddress, forceDefault, addressesLength]);

  if (!isOpen) return null;

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    // Client-side validations
    if (
      !shopName.trim() || 
      !contactPerson.trim() || 
      !phone.trim() || 
      !street.trim() || 
      !city.trim() || 
      !state.trim() || 
      !postalCode.trim() || 
      !country.trim()
    ) {
      setValidationError('All fields are required.');
      return;
    }

    const trimmedPhone = phone.trim();
    if (trimmedPhone.length < 7 || trimmedPhone.length > 20) {
      setValidationError('Phone number must be between 7 and 20 characters.');
      return;
    }

    const trimmedPostalCode = postalCode.trim();
    if (trimmedPostalCode.length < 3 || trimmedPostalCode.length > 10) {
      setValidationError('Postal code must be between 3 and 10 characters.');
      return;
    }

    const addressData = {
      shopName: shopName.trim(),
      contactPerson: contactPerson.trim(),
      phone: trimmedPhone,
      street: street.trim(),
      city: city.trim(),
      state: state.trim(),
      postalCode: trimmedPostalCode,
      country: country.trim(),
      isDefault: forceDefault ? true : isDefault
    };

    setValidationError('');
    setSaving(true);

    try {
      let updatedAddresses;
      if (mode === 'add') {
        updatedAddresses = await dispatch(addAddress({ addressData, token })).unwrap();
      } else {
        updatedAddresses = await dispatch(updateAddress({ addressId: initialAddress._id, addressData, token })).unwrap();
      }
      
      if (onSuccess) {
        onSuccess(updatedAddresses);
      }
      onClose();
    } catch (err) {
      setValidationError(err || 'Failed to save address.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <form 
        className="modal-container" 
        onClick={(e) => e.stopPropagation()} 
        onSubmit={handleFormSubmit}
      >
        <div className="modal-header">
          <h3>{mode === 'add' ? 'Add Delivery Address' : 'Edit Delivery Address'}</h3>
          <button type="button" onClick={onClose} className="modal-close-btn">&times;</button>
        </div>
        
        <div className="modal-body">
          {validationError && (
            <div style={{
              background: 'rgba(239, 68, 68, 0.15)',
              color: '#fecaca',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '8px',
              padding: '10px 14px',
              marginBottom: '16px',
              fontSize: '13.5px'
            }}>
              {validationError}
            </div>
          )}

          <div className="form-group-item">
            <label>Shop / Company Name *</label>
            <input
              type="text"
              value={shopName}
              onChange={(e) => setShopName(e.target.value)}
              placeholder="Enter shop name"
              required
            />
          </div>

          <div className="form-grid-2">
            <div className="form-group-item">
              <label>Contact Person *</label>
              <input
                type="text"
                value={contactPerson}
                onChange={(e) => setContactPerson(e.target.value)}
                placeholder="Enter full name"
                required
              />
            </div>
            <div className="form-group-item">
              <label>Phone Number *</label>
              <input
                type="text"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Enter Number"
                required
              />
            </div>
          </div>

          <div className="form-group-item">
            <label>Street Address *</label>
            <input
              type="text"
              value={street}
              onChange={(e) => setStreet(e.target.value)}
              placeholder="Suite, building, street address"
              required
            />
          </div>

          <div className="form-grid-2">
            <div className="form-group-item">
              <label>City *</label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="City"
                required
              />
            </div>
            <div className="form-group-item">
              <label>State / Province *</label>
              <input
                type="text"
                value={state}
                onChange={(e) => setState(e.target.value)}
                placeholder="State"
                required
              />
            </div>
          </div>

          <div className="form-grid-2">
            <div className="form-group-item">
              <label>Postal / ZIP Code *</label>
              <input
                type="text"
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                placeholder="Postal code"
                required
              />
            </div>
            <div className="form-group-item">
              <label>Country *</label>
              <input
                type="text"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                placeholder="Country"
                required
              />
            </div>
          </div>

          {!forceDefault && addressesLength > 0 && (
            <div className="form-group-item" style={{ marginBottom: 0 }}>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                />
                <span>Set as default delivery address</span>
              </label>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button 
            type="button" 
            onClick={onClose} 
            className="btn-secondary"
            style={{ padding: '10px 20px', borderRadius: '8px' }}
            disabled={saving}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="btn" 
            style={{ boxShadow: 'none' }}
            disabled={saving}
          >
            {saving ? 'Saving...' : mode === 'add' ? 'Add Address' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddressFormModal;
